import {
  computed,
  DestroyRef,
  effect,
  inject,
  Injectable,
  NgZone,
  PLATFORM_ID,
  signal,
  untracked,
} from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { map, Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AccountantCompaniesService } from '../company/accountant-companies.service';
import { AuthService } from '../auth/auth.service';

export type TimeCategory = 'bookkeeping' | 'vat' | 'payroll' | 'consultation' | 'other';

export interface TimeEntry {
  id: string;
  accountantId: string;
  companyId: string;
  companyName?: string;
  startedAt: string;
  stoppedAt: string | null;
  durationSeconds: number;
  description: string;
  category: TimeCategory;
  billable: boolean;
  source: 'web' | 'desktop';
  clientUuid?: string;
  createdAt: string;
  updatedAt: string;
}

export interface StartOptions {
  description?: string;
  category?: TimeCategory;
  billable?: boolean;
}

export interface StopOptions {
  description?: string;
}

export interface EntryFilters {
  from?: string;
  to?: string;
  companyId?: string;
}

export interface NewEntry {
  companyId: string;
  startedAt: string;
  stoppedAt: string;
  description?: string;
  category?: TimeCategory;
  billable?: boolean;
}

export interface EntryPatch {
  startedAt?: string;
  stoppedAt?: string;
  description?: string;
  category?: TimeCategory;
  billable?: boolean;
}

export type SummaryGroupBy = 'company' | 'day' | 'category';

export interface SummaryGroup {
  key: string;
  label: string;
  seconds: number;
  billableSeconds: number;
  amount?: number;
}

export interface ReportSummary {
  totalSeconds: number;
  billableSeconds: number;
  byGroup: SummaryGroup[];
}

export interface InvoiceDraftRequest {
  companyId: string;
  from: string;
  to: string;
  category?: TimeCategory;
  hourlyRate: number;
  roundToQuarter: boolean;
}

export interface InvoiceDraftLine {
  description: string;
  quantityHours: number;
  unitPrice: number;
  amount: number;
  category: TimeCategory;
}

export interface InvoiceDraft {
  lines: InvoiceDraftLine[];
  totalAmount: number;
  totalSeconds: number;
}

const TICK_MS = 1000;

/**
 * Śledzenie czasu pracy księgowego nad firmami klientów. Timer tyka co sekundę
 * (poza strefą Angular), a wybór firmy (nagłówek `company-id`) wyznacza, do
 * której firmy przypisany jest bieżący pomiar. Zmiana firmy podczas pomiaru
 * automatycznie zatrzymuje (flush) bieżący timer.
 */
@Injectable({ providedIn: 'root' })
export class TimeTrackingService {
  private readonly http = inject(HttpClient);
  private readonly companies = inject(AccountantCompaniesService);
  private readonly auth = inject(AuthService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly ngZone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  private readonly base = `${environment.apiUrl}/accountancy/timetracking`;

  private readonly _now = signal(Date.now());
  private readonly _runningEntry = signal<TimeEntry | null>(null);

  private intervalId: ReturnType<typeof setInterval> | null = null;

  /** Ostatnio widziana wybrana firma — do wykrycia przełączenia w effect(). */
  private previousCompanyId: string | null | undefined = undefined;
  /** Poprzedni stan uwierzytelnienia — do hydracji po zalogowaniu. */
  private previousAuth = false;
  private hydrating = false;

  readonly runningEntry = this._runningEntry.asReadonly();
  readonly running = computed(() => this._runningEntry() !== null);
  readonly currentCompanyId = computed(() => this._runningEntry()?.companyId ?? null);

  readonly elapsedSeconds = computed(() => {
    const entry = this._runningEntry();
    if (!entry) return 0;
    const started = Date.parse(entry.startedAt);
    if (Number.isNaN(started)) return 0;
    return Math.max(0, Math.floor((this._now() - started) / 1000));
  });

  readonly displayTime = computed(() => formatDuration(this.elapsedSeconds()));

  constructor() {
    this.destroyRef.onDestroy(() => this.stopTicking());

    // Auto-stop przy zmianie firmy: zależymy WYŁĄCZNIE od wybranej firmy;
    // stan timera czytamy przez untracked, aby nie tworzyć zależności.
    effect(() => {
      const current = this.companies.selectedCompanyId();
      const previous = this.previousCompanyId;
      this.previousCompanyId = current;

      if (previous === undefined || previous === current) return;

      untracked(() => {
        const entry = this._runningEntry();
        if (entry && entry.companyId === previous) {
          this.stop().subscribe({ error: () => undefined });
        }
      });
    });

    // Po zalogowaniu pobierz bieżący timer (np. wystartowany na desktopie);
    // po wylogowaniu wyczyść lokalny stan.
    effect(() => {
      const authed = this.auth.isAuthenticated();
      const previous = this.previousAuth;
      this.previousAuth = authed;
      if (authed === previous) return;
      untracked(() => {
        if (authed) {
          this.hydrate();
        } else {
          this._runningEntry.set(null);
          this.stopTicking();
        }
      });
    });
  }

  /** Pobiera bieżący uruchomiony timer księgowego (jeśli istnieje). */
  hydrate(): void {
    if (!isPlatformBrowser(this.platformId) || this.hydrating) return;
    if (!this.auth.isAuthenticated()) return;
    this.hydrating = true;
    this.http.get<{ entry: TimeEntry | null }>(`${this.base}/timers/current`).subscribe({
      next: (res) => {
        this.hydrating = false;
        const entry = res.entry ?? null;
        this._runningEntry.set(entry);
        if (entry) this.startTicking();
        else this.stopTicking();
      },
      error: () => {
        this.hydrating = false;
      },
    });
  }

  start(opts: StartOptions = {}): Observable<TimeEntry | null> {
    return this.http
      .post<{ entry: TimeEntry }>(`${this.base}/timers/start`, {
        description: opts.description,
        category: opts.category,
        billable: opts.billable,
      })
      .pipe(
        map((res) => res.entry ?? null),
        tap((entry) => {
          this._runningEntry.set(entry);
          if (entry) this.startTicking();
        }),
      );
  }

  stop(opts: StopOptions = {}): Observable<TimeEntry | null> {
    return this.http
      .post<{ entry: TimeEntry | null }>(`${this.base}/timers/stop`, { description: opts.description })
      .pipe(
        map((res) => res.entry ?? null),
        tap(() => {
          this._runningEntry.set(null);
          this.stopTicking();
        }),
      );
  }

  list(filters: EntryFilters = {}): Observable<TimeEntry[]> {
    let params = new HttpParams();
    if (filters.from) params = params.set('from', filters.from);
    if (filters.to) params = params.set('to', filters.to);
    if (filters.companyId) params = params.set('companyId', filters.companyId);
    return this.http
      .get<{ data: TimeEntry[] }>(`${this.base}/entries`, { params })
      .pipe(map((res) => res.data ?? []));
  }

  add(entry: NewEntry): Observable<TimeEntry | null> {
    return this.http
      .post<{ entry: TimeEntry }>(`${this.base}/entries`, entry)
      .pipe(map((res) => res.entry ?? null));
  }

  update(id: string, patch: EntryPatch): Observable<TimeEntry | null> {
    return this.http
      .patch<{ entry: TimeEntry }>(`${this.base}/entries/${id}`, patch)
      .pipe(map((res) => res.entry ?? null));
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/entries/${id}`);
  }

  summary(from: string, to: string, groupBy: SummaryGroupBy = 'company'): Observable<ReportSummary> {
    const params = new HttpParams().set('from', from).set('to', to).set('groupBy', groupBy);
    return this.http.get<ReportSummary>(`${this.base}/reports/summary`, { params });
  }

  /** Generuje szkic pozycji faktury na podstawie rozliczalnych godzin. */
  invoiceDraft(req: InvoiceDraftRequest): Observable<InvoiceDraft> {
    return this.http.post<InvoiceDraft>(`${this.base}/invoice-draft`, req);
  }

  private startTicking(): void {
    if (!isPlatformBrowser(this.platformId) || this.intervalId !== null) return;
    this._now.set(Date.now());
    this.ngZone.runOutsideAngular(() => {
      this.intervalId = setInterval(
        () => this.ngZone.run(() => this._now.set(Date.now())),
        TICK_MS,
      );
    });
  }

  private stopTicking(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
}

export function formatDuration(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}
